import { makeAutoObservable } from 'mobx';

// Platform detection types
export type Platform = 'desktop' | 'mobile' | 'tablet' | 'unknown';
export type Browser = 'chrome' | 'firefox' | 'safari' | 'edge' | 'opera' | 'unknown';
export type OS = 'windows' | 'macos' | 'linux' | 'android' | 'ios' | 'unknown';

// API categories
export type ApiCategory = 
  | 'communication' 
  | 'storage' 
  | 'media' 
  | 'device' 
  | 'graphics' 
  | 'network' 
  | 'security' 
  | 'location' 
  | 'payment' 
  | 'performance' 
  | 'worker' 
  | 'pwa';

// Base API interfaces
export interface BaseApi {
  apiAvailable: boolean;
  category: ApiCategory;
  description: string;
  supportedPlatforms: Platform[];
  supportedBrowsers: Browser[];
}

export interface ApiWithInstances extends BaseApi {
  instances: Record<string, any>;
}

export interface ApiWithPermission extends BaseApi {
  permissionResult: PermissionState | 'default' | 'error';
}

export interface PlatformInfo {
  platform: Platform;
  browser: Browser;
  os: OS;
  userAgent: string;
  isMobile: boolean;
  isDesktop: boolean;
  version: string;
}
class WebApiStore {
  rootStore: any;
  
  // Platform information
  platformInfo: PlatformInfo;
  
  // Communication APIs
  broadcastChannelApi: ApiWithInstances;
  notificationApi: ApiWithPermission;
  webRtcApi: BaseApi;
  webSocketApi: BaseApi;
  serverSentEventsApi: BaseApi;
  
  // Storage APIs
  localStorageApi: BaseApi;
  sessionStorageApi: BaseApi;
  indexedDbApi: BaseApi;
  cacheApi: BaseApi;
  cookieStoreApi: BaseApi;
  
  // Media APIs
  mediaStreamApi: BaseApi;
  mediaRecorderApi: BaseApi;
  webAudioApi: BaseApi;
  webMidiApi: BaseApi;
  barcodeDetectorApi: ApiWithInstances;
  
  // Device APIs
  geolocationApi: ApiWithPermission;
  deviceOrientationApi: BaseApi;
  vibrationApi: BaseApi;
  batteryApi: BaseApi;
  gamepadApi: BaseApi;
  bluetoothApi: BaseApi;
  usbApi: BaseApi;
  serialApi: BaseApi;
  
  // Graphics APIs
  canvasApi: BaseApi;
  webGlApi: BaseApi;
  webGl2Api: BaseApi;
  webGpuApi: BaseApi;
  
  // Network APIs
  fetchApi: BaseApi;
  backgroundSyncApi: BaseApi;
  backgroundFetchApi: BaseApi;
  
  // Security APIs
  webCryptoApi: BaseApi;
  credentialsApi: BaseApi;
  
  // Payment APIs
  paymentRequestApi: BaseApi;
  
  // Performance APIs
  performanceApi: BaseApi;
  performanceObserverApi: BaseApi;
  intersectionObserverApi: BaseApi;
  resizeObserverApi: BaseApi;
  
  // Worker APIs
  webWorkerApi: BaseApi;
  serviceWorkerApi: BaseApi;
  sharedWorkerApi: BaseApi;
  
  // PWA APIs
  webAppManifestApi: BaseApi;
  installPromptApi: BaseApi;
  orientationLockApi: BaseApi;
  fullscreenApi: BaseApi;
  wakeLockApi: BaseApi;

  constructor(rootStore: any) {
    this.rootStore = rootStore;
    this.platformInfo = this.detectPlatform();
    this.initializeApis();
    this.loadAvailableApis();
    makeAutoObservable(this, { rootStore: false });
  }

  private detectPlatform(): PlatformInfo {
    const userAgent = navigator.userAgent;
    
    // Detect OS
    let os: OS = 'unknown';
    if (/Windows/.test(userAgent)) os = 'windows';
    else if (/Mac|iPhone|iPad|iPod/.test(userAgent)) os = /iPhone|iPad|iPod/.test(userAgent) ? 'ios' : 'macos';
    else if (/Linux/.test(userAgent)) os = 'linux';
    else if (/Android/.test(userAgent)) os = 'android';
    
    // Detect browser
    let browser: Browser = 'unknown';
    let version = '';
    if (/Chrome/.test(userAgent) && !/Edge|OPR/.test(userAgent)) {
      browser = 'chrome';
      version = userAgent.match(/Chrome\/([\d.]+)/)?.[1] || '';
    } else if (/Firefox/.test(userAgent)) {
      browser = 'firefox';
      version = userAgent.match(/Firefox\/([\d.]+)/)?.[1] || '';
    } else if (/Safari/.test(userAgent) && !/Chrome/.test(userAgent)) {
      browser = 'safari';
      version = userAgent.match(/Version\/([\d.]+)/)?.[1] || '';
    } else if (/Edge/.test(userAgent)) {
      browser = 'edge';
      version = userAgent.match(/Edge\/([\d.]+)/)?.[1] || '';
    } else if (/OPR/.test(userAgent)) {
      browser = 'opera';
      version = userAgent.match(/OPR\/([\d.]+)/)?.[1] || '';
    }
    
    // Detect platform type
    const isMobile = /Mobile|Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/.test(userAgent);
    const isTablet = /iPad|Android(?!.*Mobile)/.test(userAgent);
    
    let platformType: Platform = 'unknown';
    if (isTablet) platformType = 'tablet';
    else if (isMobile) platformType = 'mobile';
    else platformType = 'desktop';
    
    return {
      platform: platformType,
      browser,
      os,
      userAgent,
      isMobile,
      isDesktop: !isMobile && !isTablet,
      version
    };
  }

  private initializeApis(): void {
    // Initialize all API objects with default values
    this.broadcastChannelApi = this.createApiWithInstances('communication', 'Cross-tab communication via BroadcastChannel');
    this.notificationApi = this.createApiWithPermission('communication', 'Browser notifications');
    this.webRtcApi = this.createBaseApi('communication', 'Real-time peer-to-peer communication');
    this.webSocketApi = this.createBaseApi('communication', 'Real-time bidirectional communication');
    this.serverSentEventsApi = this.createBaseApi('communication', 'Server-sent events');
    
    this.localStorageApi = this.createBaseApi('storage', 'Local storage for persistent data');
    this.sessionStorageApi = this.createBaseApi('storage', 'Session storage for temporary data');
    this.indexedDbApi = this.createBaseApi('storage', 'Client-side database');
    this.cacheApi = this.createBaseApi('storage', 'Cache API for offline functionality');
    this.cookieStoreApi = this.createBaseApi('storage', 'Asynchronous cookie access');
    
    this.mediaStreamApi = this.createBaseApi('media', 'Access camera and microphone');
    this.mediaRecorderApi = this.createBaseApi('media', 'Record audio and video streams');
    this.webAudioApi = this.createBaseApi('media', 'Audio processing and synthesis');
    this.webMidiApi = this.createBaseApi('media', 'MIDI device access');
    this.barcodeDetectorApi = this.createApiWithInstances('media', 'Barcode and QR code detection');
    
    this.geolocationApi = this.createApiWithPermission('location', 'Device location access');
    this.deviceOrientationApi = this.createBaseApi('device', 'Device orientation sensors');
    this.vibrationApi = this.createBaseApi('device', 'Device vibration control');
    this.batteryApi = this.createBaseApi('device', 'Battery status information');
    this.gamepadApi = this.createBaseApi('device', 'Gamepad input support');
    this.bluetoothApi = this.createBaseApi('device', 'Bluetooth device access');
    this.usbApi = this.createBaseApi('device', 'USB device access');
    this.serialApi = this.createBaseApi('device', 'Serial port communication');
    
    this.canvasApi = this.createBaseApi('graphics', '2D graphics rendering');
    this.webGlApi = this.createBaseApi('graphics', '3D graphics rendering');
    this.webGl2Api = this.createBaseApi('graphics', 'Enhanced 3D graphics rendering');
    this.webGpuApi = this.createBaseApi('graphics', 'Modern GPU access');
    
    this.fetchApi = this.createBaseApi('network', 'Modern HTTP client');
    this.backgroundSyncApi = this.createBaseApi('network', 'Background data synchronization');
    this.backgroundFetchApi = this.createBaseApi('network', 'Background file downloads');
    
    this.webCryptoApi = this.createBaseApi('security', 'Cryptographic operations');
    this.credentialsApi = this.createBaseApi('security', 'Credential management');
    
    this.paymentRequestApi = this.createBaseApi('payment', 'Payment processing');
    
    this.performanceApi = this.createBaseApi('performance', 'Performance measurement');
    this.performanceObserverApi = this.createBaseApi('performance', 'Performance monitoring');
    this.intersectionObserverApi = this.createBaseApi('performance', 'Element intersection detection');
    this.resizeObserverApi = this.createBaseApi('performance', 'Element resize detection');
    
    this.webWorkerApi = this.createBaseApi('worker', 'Background JavaScript execution');
    this.serviceWorkerApi = this.createBaseApi('worker', 'Background service worker');
    this.sharedWorkerApi = this.createBaseApi('worker', 'Shared worker threads');
    
    this.webAppManifestApi = this.createBaseApi('pwa', 'Web app manifest support');
    this.installPromptApi = this.createBaseApi('pwa', 'App installation prompts');
    this.orientationLockApi = this.createBaseApi('pwa', 'Screen orientation control');
    this.fullscreenApi = this.createBaseApi('pwa', 'Fullscreen mode support');
    this.wakeLockApi = this.createBaseApi('pwa', 'Screen wake lock');
  }

  private createBaseApi(category: ApiCategory, description: string): BaseApi {
    return {
      apiAvailable: false,
      category,
      description,
      supportedPlatforms: ['desktop', 'mobile', 'tablet'],
      supportedBrowsers: ['chrome', 'firefox', 'safari', 'edge', 'opera']
    };
  }

  private createApiWithInstances(category: ApiCategory, description: string): ApiWithInstances {
    return {
      ...this.createBaseApi(category, description),
      instances: {}
    };
  }

  private createApiWithPermission(category: ApiCategory, description: string): ApiWithPermission {
    return {
      ...this.createBaseApi(category, description),
      permissionResult: 'default'
    };
  }

  private loadAvailableApis(): void {
    // Communication APIs
    this.broadcastChannelApi.apiAvailable = 'BroadcastChannel' in window;
    this.notificationApi.apiAvailable = 'Notification' in window;
    if (this.notificationApi.apiAvailable) {
      this.notificationApi.permissionResult = Notification.permission;
    }
    this.webRtcApi.apiAvailable = 'RTCPeerConnection' in window;
    this.webSocketApi.apiAvailable = 'WebSocket' in window;
    this.serverSentEventsApi.apiAvailable = 'EventSource' in window;
    
    // Storage APIs
    this.localStorageApi.apiAvailable = 'localStorage' in window;
    this.sessionStorageApi.apiAvailable = 'sessionStorage' in window;
    this.indexedDbApi.apiAvailable = 'indexedDB' in window;
    this.cacheApi.apiAvailable = 'caches' in window;
    this.cookieStoreApi.apiAvailable = 'cookieStore' in window;
    
    // Media APIs
    this.mediaStreamApi.apiAvailable = !!(navigator.mediaDevices?.getUserMedia);
    this.mediaRecorderApi.apiAvailable = 'MediaRecorder' in window;
    this.webAudioApi.apiAvailable = 'AudioContext' in window || 'webkitAudioContext' in window;
    this.webMidiApi.apiAvailable = !!(navigator.requestMIDIAccess);
    this.barcodeDetectorApi.apiAvailable = 'BarcodeDetector' in window;
    
    // Device APIs
    this.geolocationApi.apiAvailable = !!(navigator.geolocation);
    if (this.geolocationApi.apiAvailable) {
      navigator.permissions?.query({ name: 'geolocation' as PermissionName })
        .then(result => this.geolocationApi.permissionResult = result.state)
        .catch(() => this.geolocationApi.permissionResult = 'default');
    }
    this.deviceOrientationApi.apiAvailable = 'DeviceOrientationEvent' in window;
    this.vibrationApi.apiAvailable = !!((navigator as any).vibrate);
    this.batteryApi.apiAvailable = !!((navigator as any).getBattery);
    this.gamepadApi.apiAvailable = !!(navigator.getGamepads);
    this.bluetoothApi.apiAvailable = !!((navigator as any).bluetooth);
    this.usbApi.apiAvailable = !!((navigator as any).usb);
    this.serialApi.apiAvailable = !!((navigator as any).serial);
    
    // Graphics APIs
    this.canvasApi.apiAvailable = !!document.createElement('canvas').getContext;
    const canvas = document.createElement('canvas');
    this.webGlApi.apiAvailable = !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
    this.webGl2Api.apiAvailable = !!canvas.getContext('webgl2');
    this.webGpuApi.apiAvailable = !!((navigator as any).gpu);
    
    // Network APIs
    this.fetchApi.apiAvailable = 'fetch' in window;
    this.backgroundSyncApi.apiAvailable = 'serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration?.prototype;
    this.backgroundFetchApi.apiAvailable = 'serviceWorker' in navigator && 'BackgroundFetch' in window;
    
    // Security APIs
    this.webCryptoApi.apiAvailable = !!(window.crypto?.subtle);
    this.credentialsApi.apiAvailable = !!(navigator.credentials);
    
    // Payment APIs
    this.paymentRequestApi.apiAvailable = 'PaymentRequest' in window;
    
    // Performance APIs
    this.performanceApi.apiAvailable = 'performance' in window;
    this.performanceObserverApi.apiAvailable = 'PerformanceObserver' in window;
    this.intersectionObserverApi.apiAvailable = 'IntersectionObserver' in window;
    this.resizeObserverApi.apiAvailable = 'ResizeObserver' in window;
    
    // Worker APIs
    this.webWorkerApi.apiAvailable = 'Worker' in window;
    this.serviceWorkerApi.apiAvailable = 'serviceWorker' in navigator;
    this.sharedWorkerApi.apiAvailable = 'SharedWorker' in window;
    
    // PWA APIs
    this.webAppManifestApi.apiAvailable = 'applicationCache' in window || 'serviceWorker' in navigator;
    this.installPromptApi.apiAvailable = this.platformInfo.browser === 'chrome' || this.platformInfo.browser === 'edge';
    this.orientationLockApi.apiAvailable = !!((screen.orientation as any)?.lock);
    this.fullscreenApi.apiAvailable = !!(document.documentElement.requestFullscreen);
    this.wakeLockApi.apiAvailable = !!((navigator as any).wakeLock);
  }

  // Enhanced reporting methods
  reportAvailableApis = () => {
    const report = {
      platform: this.platformInfo,
      availableApis: this.getAvailableApisByCategory(),
      summary: this.getApiSummary()
    };
    
    if (console.table) {
      console.log('Platform Information:', this.platformInfo);
      console.table(this.getApiSummary());
      console.log('Detailed API Report:', report);
    }
    return report;
  };

  getAvailableApisByCategory = () => {
    const categories: Record<ApiCategory, string[]> = {
      communication: [],
      storage: [],
      media: [],
      device: [],
      graphics: [],
      network: [],
      security: [],
      location: [],
      payment: [],
      performance: [],
      worker: [],
      pwa: []
    };

    // Group APIs by category
    Object.entries(this.getAllApis()).forEach(([name, api]) => {
      if (api.apiAvailable) {
        categories[api.category].push(name);
      }
    });

    return categories;
  };

  getApiSummary = () => {
    const apis = this.getAllApis();
    const summary: Record<string, boolean> = {};
    
    Object.entries(apis).forEach(([name, api]) => {
      summary[name] = api.apiAvailable;
    });
    
    return summary;
  };

  getAllApis = () => {
    return {
      // Communication
      broadcastChannelApi: this.broadcastChannelApi,
      notificationApi: this.notificationApi,
      webRtcApi: this.webRtcApi,
      webSocketApi: this.webSocketApi,
      serverSentEventsApi: this.serverSentEventsApi,
      
      // Storage
      localStorageApi: this.localStorageApi,
      sessionStorageApi: this.sessionStorageApi,
      indexedDbApi: this.indexedDbApi,
      cacheApi: this.cacheApi,
      cookieStoreApi: this.cookieStoreApi,
      
      // Media
      mediaStreamApi: this.mediaStreamApi,
      mediaRecorderApi: this.mediaRecorderApi,
      webAudioApi: this.webAudioApi,
      webMidiApi: this.webMidiApi,
      barcodeDetectorApi: this.barcodeDetectorApi,
      
      // Device
      geolocationApi: this.geolocationApi,
      deviceOrientationApi: this.deviceOrientationApi,
      vibrationApi: this.vibrationApi,
      batteryApi: this.batteryApi,
      gamepadApi: this.gamepadApi,
      bluetoothApi: this.bluetoothApi,
      usbApi: this.usbApi,
      serialApi: this.serialApi,
      
      // Graphics
      canvasApi: this.canvasApi,
      webGlApi: this.webGlApi,
      webGl2Api: this.webGl2Api,
      webGpuApi: this.webGpuApi,
      
      // Network
      fetchApi: this.fetchApi,
      backgroundSyncApi: this.backgroundSyncApi,
      backgroundFetchApi: this.backgroundFetchApi,
      
      // Security
      webCryptoApi: this.webCryptoApi,
      credentialsApi: this.credentialsApi,
      
      // Payment
      paymentRequestApi: this.paymentRequestApi,
      
      // Performance
      performanceApi: this.performanceApi,
      performanceObserverApi: this.performanceObserverApi,
      intersectionObserverApi: this.intersectionObserverApi,
      resizeObserverApi: this.resizeObserverApi,
      
      // Worker
      webWorkerApi: this.webWorkerApi,
      serviceWorkerApi: this.serviceWorkerApi,
      sharedWorkerApi: this.sharedWorkerApi,
      
      // PWA
      webAppManifestApi: this.webAppManifestApi,
      installPromptApi: this.installPromptApi,
      orientationLockApi: this.orientationLockApi,
      fullscreenApi: this.fullscreenApi,
      wakeLockApi: this.wakeLockApi
    };
  };

  // Legacy methods (preserved for backwards compatibility)
  createBarcodeDetector = (name: string) => {
    if (!this.barcodeDetectorApi.apiAvailable) {
      console.error('barcodeDetectorApi isnt available on this device');
      return;
    }
    const barcodeDetector = new (window as any).BarcodeDetector({
      formats: ['qr_code'],
    });
    this.barcodeDetectorApi.instances[name] = {
      barcodeDetector: barcodeDetector,
      results: '',
    };
    return barcodeDetector;
  };

  initiateBroadcastChannel = (name: string, handler?: (data: any) => void) => {
    if (!this.broadcastChannelApi.apiAvailable) {
      console.error('broadcastChannelApi isnt available on this device');
      return;
    }
    if (!name) {
      console.error('broadcast channel requires a name');
      return;
    }
    this.broadcastChannelApi.instances[name] = new BroadcastChannel(name);
    if (handler) {
      this.setBroadcastChannelHandler(name, handler);
    }
  };

  setBroadcastChannelHandler = (name: string, handler: (data: any) => void) => {
    if (!this.broadcastChannelApi.apiAvailable) {
      console.error('broadcastChannelApi isnt available on this device');
      return;
    }
    if (!name) {
      console.error('broadcast channel requires a name');
      return;
    }
    if (!handler) {
      console.error('broadcast channel requires a handler');
      return;
    }
    this.broadcastChannelApi.instances[name].onmessage = (message: MessageEvent) => 
      handler(message.data);
    return name;
  };

  broadcastChannelPostMessage = (name: string, data: any) => {
    if (!this.broadcastChannelApi.apiAvailable) {
      console.error('broadcastChannelApi isnt available on this device');
      return;
    }
    if (!name) {
      console.error('broadcast channel requires a name');
      return;
    }
    if (!data) {
      console.error('broadcast channel requires data');
      return;
    }
    return this.broadcastChannelApi.instances[name].postMessage(data);
  };

  requestNotificationPermission = async (): Promise<NotificationPermission | 'error'> => {
    if (!this.notificationApi.apiAvailable) {
      console.error('notificationApi isnt available on this device');
      return 'error';
    }
    if (Notification.permission === 'granted') {
      if (this.rootStore.notificationStore) {
        this.rootStore.notificationStore.addNotification({
          message: 'Notifications have been enabled on this device.',
          options: { variant: 'success' },
        });
      }
      if (this.notificationApi.permissionResult !== 'granted') {
        this.notificationApi.permissionResult = 'granted';
      }
      return 'granted';
    }
    if (Notification.permission !== 'denied') {
      const permissionResult = await Notification.requestPermission();
      if (
        permissionResult &&
        permissionResult !== this.notificationApi.permissionResult
      ) {
        this.notificationApi.permissionResult = permissionResult;
      }
      return permissionResult;
    }

    if (Notification.permission === 'denied') {
      if (this.rootStore.notificationStore) {
        this.rootStore.notificationStore.addNotification({
          message:
            'Notifications have been denied on this device, please reset permissions and try again.',
          options: { variant: 'warning' },
        });
      }
      return 'denied';
    }
    return 'error';
  };

  // Computed getter for backwards compatibility
  get availableApis() {
    return this.getApiSummary();
  }
}

export default WebApiStore;
